import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Mail } from 'lucide-react';
import { LandingHero } from './landing/LandingHero';
import { LandingBenefits } from './landing/LandingBenefits';
import { LandingFeatures } from './landing/LandingFeatures';
import { LandingAIFeatures } from './landing/LandingAIFeatures';
import { LandingLCN } from './landing/LandingLCN';
import { LandingTryChat } from './landing/LandingTryChat';

import { LandingPricing } from './landing/LandingPricing';
import { LandingFAQ } from './landing/LandingFAQ';
import { LandingCTA } from './landing/LandingCTA';
import SeoHead from './SeoHead';

export const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const language = i18n.language?.split('-')[0] === 'ar' ? 'ar' : 'en';

  useEffect(() => {
    if (language === 'ar' && window.location.pathname === '/') {
      navigate('/ar', { replace: true });
    }
  }, [language, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={language === 'ar' ? 'شحنات - منصة تعاونية للشحن بالذكاء الاصطناعي' : 'Shohnat - AI-powered Freight Collaboration Platform'}
        description={language === 'ar' ? 'منصة تربط الشاحنين بشركات النقل باستخدام الذكاء الاصطناعي.' : 'AI-powered freight collaboration platform connecting shippers with carriers.'}
        canonicalPath={language === 'ar' ? '/ar' : '/'}
        locale={language as 'en' | 'ar'}
      />

      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg flex-shrink-0">
              <img src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" alt="Shohnat" className="h-7 sm:h-8 w-auto" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="outline" onClick={() => navigate('/auth')} className="hidden sm:flex hover-scale border-border hover:bg-muted">
              {t('auth.signIn')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <LandingHero />
      <LandingTryChat />
      <LandingBenefits />
      <LandingLCN />
      <LandingFeatures />
      <LandingPricing />
      <LandingAIFeatures />
      <LandingFAQ />
      <LandingCTA />

      {/* Footer */}
      <footer className="bg-gradient-to-r from-black to-gray-700 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex justify-center">
            <div className="flex flex-col sm:flex-row items-center justify-between w-full max-w-4xl bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 shadow-xl py-8 px-6 gap-4">
              {/* Left: Logo */}
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <img src="/lovable-uploads/731a4d5a-546d-41f3-a979-93b9f45689bf.png" alt="Shohnat" className="h-10 w-auto" />
                </div>
              </div>
              
              {/* Center: Year */}
              <div className="flex items-center">
                <span className="text-xs text-white/50">© 2025</span>
              </div>
              
              {/* Right: Email */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-white/70" />
                  <span className="text-sm text-white/70">info@shohnat.app</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};